import {
  Component,
  ChangeDetectionStrategy,
  input,
  signal,
  computed,
  inject,
  ViewChild,
  ElementRef,
  effect
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { InputTextModule } from 'primeng/inputtext';
import { FloatLabelModule } from 'primeng/floatlabel';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { EditorModule } from 'primeng/editor';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../services/auth.service';
import { CompanyService } from '../../services/company.service';
import {
  EmailSignatureService,
  type EmailSignatureDTO
} from '../../services/email-signature.service';
import { apiFailureMessage, httpErrorApiMessage } from '../../core/api-response-utils';
import type { QuillEditorInstance } from '../../core/quill-mention-setup';
import {
  EMAIL_SIGNATURE_PRESETS,
  SOCIAL_NETWORKS,
  CompanyEmailSignature,
  EmailSignatureFieldValues,
  EmailSignatureLayoutId,
  buildSignatureHtml,
  defaultSignatureFields,
  layoutUsesPhoto,
  layoutUsesLogo,
  layoutUsesTextImage,
  SIGN_IMAGE_WIDTH_DEFAULT,
  SIGN_IMAGE_WIDTH_MIN,
  SIGN_IMAGE_WIDTH_MAX,
  clampSignImageWidth,
  buildSignaturePreviewContext,
  renderSignatureHandlebars,
  rewriteSignatureIconUrls,
  SIGNATURE_USER_VARS
} from './email-signature-templates';

type EditorStep = 'pick-layout' | 'edit';

@Component({
  selector: 'app-company-email-signatures',
  templateUrl: './company-email-signatures.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      :host ::ng-deep .signature-editor-shell.html-mode .ql-container { display: none; }
      :host ::ng-deep .p-editor-toolbar {
        background: rgb(248 250 252);
        border-bottom: 1px solid rgb(226 232 240);
        display: flex;
        flex-wrap: wrap;
        gap: 0.25rem;
        row-gap: 0.5rem;
      }
      :host ::ng-deep .p-editor-toolbar .pento-html-source-formats {
        margin-left: auto;
        margin-right: 0;
      }
      :host ::ng-deep .pento-html-source-toggle {
        width: 28px;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        font-size: 13px;
        font-weight: 600;
        line-height: 1;
      }
      :host ::ng-deep .pento-html-source-toggle.ql-active { color: #06c; }
      :host ::ng-deep .editor-html-source {
        width: 100%;
        height: 280px;
        min-height: 280px;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        font-size: 0.8125rem;
        line-height: 1.5;
        border: none;
        border-top: 1px solid rgb(226 232 240);
        padding: 0.75rem 0.9375rem;
        background: white;
        color: rgb(15 23 42);
        resize: vertical;
        box-sizing: border-box;
        outline: none;
      }
      :host ::ng-deep .ql-editor {
        font-size: 0.875rem;
        line-height: 1.5;
        min-height: 220px;
      }
      :host ::ng-deep .signature-intro-editor .ql-editor {
        min-height: 100px;
        max-height: 140px;
        overflow-y: auto;
        font-size: 0.8125rem;
      }
      :host ::ng-deep .signature-intro-editor .p-editor-toolbar {
        padding: 0.35rem 0.5rem;
      }
      :host ::ng-deep .signature-intro-editor .p-editor,
      :host ::ng-deep .signature-intro-editor .p-editor-container {
        overflow: hidden;
      }
      :host ::ng-deep .signature-body-editor .ql-editor {
        min-height: 160px;
        max-height: 240px;
        overflow-y: auto;
        font-size: 0.875rem;
      }
      :host ::ng-deep .signature-preview-panel {
        display: flex;
        flex-direction: column;
        min-height: 0;
        max-height: min(32rem, 52vh);
        overflow: hidden;
      }
      :host ::ng-deep .signature-preview-scroll {
        flex: 1 1 auto;
        min-height: 0;
        overflow: auto;
        overscroll-behavior: contain;
      }
      :host ::ng-deep .signature-preview-body {
        overflow-wrap: anywhere;
        word-break: break-word;
      }
      :host ::ng-deep .signature-preview-body table {
        max-width: 100% !important;
        width: auto !important;
        table-layout: fixed;
      }
      :host ::ng-deep .signature-preview-body td,
      :host ::ng-deep .signature-preview-body th {
        word-break: break-word;
        overflow-wrap: anywhere;
      }
      :host ::ng-deep .signature-preview-body img:not(.sign-image) {
        max-width: 72px !important;
        width: auto !important;
        height: auto !important;
        object-fit: cover;
      }
      :host ::ng-deep .signature-preview-body img.sign-image {
        max-width: 100% !important;
        height: auto !important;
        object-fit: contain;
      }
      :host ::ng-deep .signature-editor-shell {
        overflow: hidden;
        max-width: 100%;
      }
      :host ::ng-deep .signature-editor-shell .p-editor,
      :host ::ng-deep .signature-editor-shell .p-editor-container {
        max-width: 100%;
        overflow: hidden;
      }
      :host ::ng-deep .signature-editor-shell .ql-editor {
        overflow-x: hidden;
        overflow-wrap: anywhere;
        word-break: break-word;
      }
    `
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    FloatLabelModule,
    ButtonModule,
    TooltipModule,
    TagModule,
    EditorModule
  ]
})
export class CompanyEmailSignaturesComponent {
  @ViewChild('signatureHtmlSource') signatureHtmlSource?: ElementRef<HTMLTextAreaElement>;

  private fb = inject(FormBuilder);
  private toastr = inject(ToastrService);
  private sanitizer = inject(DomSanitizer);
  private authService = inject(AuthService);
  private companyService = inject(CompanyService);
  private emailSignatureService = inject(EmailSignatureService);

  /** Datos de la empresa para rellenar defaults al crear una firma. */
  companyId = input<number | null>(null);
  companyName = input<string>('');
  companyEmail = input<string>('');
  companyPhone = input<string>('');
  logoUrl = input<string | null>(null);
  enabled = input<boolean>(true);

  readonly presets = EMAIL_SIGNATURE_PRESETS;
  readonly socialNetworks = SOCIAL_NETWORKS;
  readonly editorStyle = { height: '280px' };
  readonly introEditorStyle = { height: '140px' };
  readonly bodyEditorStyle = { height: '200px' };
  readonly signImageWidthMin = SIGN_IMAGE_WIDTH_MIN;
  readonly signImageWidthMax = SIGN_IMAGE_WIDTH_MAX;
  readonly userVars = SIGNATURE_USER_VARS;

  signatures = signal<CompanyEmailSignature[]>([]);
  loadingList = signal(false);
  saving = signal(false);
  showEditor = signal(false);
  editorShown = signal(false);
  editorStep = signal<EditorStep>('pick-layout');
  editingId = signal<string | null>(null);
  selectedLayoutId = signal<EmailSignatureLayoutId | null>(null);
  freeEditUnlocked = signal(false);
  editorHtmlMode = signal(false);
  editorHtmlSource = '';
  previewHtml = signal('');
  quillInstance: QuillEditorInstance | null = null;
  /** Evita que Quill reescriba el HTML canónico al hacer setContents / init. */
  private suppressQuillWriteback = false;

  deleteConfirmOpen = signal(false);
  deleteConfirmShown = signal(false);
  deleteItem = signal<CompanyEmailSignature | null>(null);
  uploadingSignImage = signal(false);

  signatureForm: FormGroup;
  fieldsForm: FormGroup;

  selectedPreset = computed(() =>
    this.presets.find((p) => p.id === this.selectedLayoutId()) ?? null
  );

  isBlankLayout = computed(() => this.selectedPreset()?.blank === true);

  showFieldCustomization = computed(
    () => !this.isBlankLayout() && !this.freeEditUnlocked() && this.editorStep() === 'edit'
  );

  showPhotoField = computed(() => layoutUsesPhoto(this.selectedLayoutId()));
  showLogoField = computed(() => layoutUsesLogo(this.selectedLayoutId()));
  isTextImageLayout = computed(() => layoutUsesTextImage(this.selectedLayoutId()));
  showClassicFields = computed(() => this.showFieldCustomization() && !this.isTextImageLayout());
  showTextImageFields = computed(() => this.showFieldCustomization() && this.isTextImageLayout());

  /** Foto del usuario autenticado (solo lectura en firmas). */
  profilePhotoUrl = computed(() => this.authService.getUserPicture() || '');

  /** Logo de la empresa (solo lectura en firmas). */
  companyLogoUrl = computed(() => this.logoUrl() || '');

  previewSafeHtml = computed<SafeHtml>(() => {
    const html = this.previewHtml();
    return this.sanitizer.bypassSecurityTrustHtml(
      html || '<p style="color:#94a3b8;font-size:13px;">Vista previa vacía</p>'
    );
  });

  constructor() {
    this.signatureForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      html: ['']
    });

    this.fieldsForm = this.fb.group({
      fullName: ['', [Validators.required, Validators.maxLength(150)]],
      jobTitle: ['', [Validators.maxLength(150)]],
      companyName: ['', [Validators.maxLength(200)]],
      email: ['', [Validators.maxLength(100)]],
      phone: ['', [Validators.maxLength(40)]],
      website: ['', [Validators.maxLength(200)]],
      accentColor: ['#0ea5e9'],
      logoUrl: [{ value: '', disabled: true }],
      photoUrl: [{ value: '', disabled: true }],
      introHtml: [''],
      bodyHtml: ['<p>Escribe aquí el texto de tu firma.</p>'],
      signImageUrl: [''],
      signImageWidth: [SIGN_IMAGE_WIDTH_DEFAULT, [Validators.min(SIGN_IMAGE_WIDTH_MIN), Validators.max(SIGN_IMAGE_WIDTH_MAX)]],
      socialLinkedin: ['', [Validators.maxLength(300)]],
      socialFacebook: ['', [Validators.maxLength(300)]],
      socialX: ['', [Validators.maxLength(300)]],
      socialInstagram: ['', [Validators.maxLength(300)]],
      socialYoutube: ['', [Validators.maxLength(300)]],
      socialWhatsapp: ['', [Validators.maxLength(300)]],
      socialTiktok: ['', [Validators.maxLength(300)]],
      socialMessenger: ['', [Validators.maxLength(300)]]
    });

    this.fieldsForm.valueChanges.subscribe(() => {
      if (this.showFieldCustomization()) {
        this.regenerateHtmlFromFields();
      }
    });

    this.signatureForm.get('html')?.valueChanges.subscribe((html: string) => {
      this.previewHtml.set(html ?? '');
    });

    effect(() => {
      if (this.enabled()) {
        this.loadSignatures();
      } else {
        this.signatures.set([]);
      }
    });
  }

  private loadSignatures(): void {
    this.loadingList.set(true);
    this.emailSignatureService.list().subscribe({
      next: (res) => {
        this.loadingList.set(false);
        if (!res.ok || !res.body) {
          this.signatures.set([]);
          return;
        }
        const items = res.body.map((dto) => this.fromDto(dto));
        this.signatures.set(items);
        this.healOutdatedIconUrls(res.body);
      },
      error: (err) => {
        this.loadingList.set(false);
        this.signatures.set([]);
        this.toastr.error(httpErrorApiMessage(err, 'No se pudieron cargar las firmas'));
      }
    });
  }

  /** Actualiza firmas guardadas para que usen el bucket público de iconos. */
  private healOutdatedIconUrls(dtos: EmailSignatureDTO[]): void {
    for (const dto of dtos) {
      let fields = defaultSignatureFields(this.companyDefaults());
      if (dto.fieldsJson) {
        try {
          fields = defaultSignatureFields(JSON.parse(dto.fieldsJson) as Partial<EmailSignatureFieldValues>);
        } catch {
          // ignore
        }
      }

      let html: string | null = null;
      const hasLegacyAssetPath = /nucleoapp\/|assets\/images\/icons\/nucleoapp\//i.test(dto.html || '');
      if (!dto.customHtml && hasLegacyAssetPath) {
        html = buildSignatureHtml((dto.layoutId as EmailSignatureLayoutId) || 'classic', fields, 'handlebars');
      } else {
        const rewritten = rewriteSignatureIconUrls(dto.html || '');
        if (rewritten !== dto.html) {
          html = rewritten;
        }
      }

      if (!html || html === dto.html) {
        continue;
      }

      this.emailSignatureService
        .update(dto.id, {
          name: dto.name,
          layoutId: dto.layoutId,
          html,
          fieldsJson: dto.fieldsJson ?? JSON.stringify(fields),
          customHtml: dto.customHtml
        })
        .subscribe({
          next: (res) => {
            if (!res.ok || !res.body) {
              return;
            }
            this.signatures.update((list) =>
              list.map((sig) => (sig.id === String(dto.id) ? this.fromDto(res.body!) : sig))
            );
          }
        });
    }
  }

  private fromDto(dto: EmailSignatureDTO): CompanyEmailSignature {
    let fields = defaultSignatureFields(this.companyDefaults());
    if (dto.fieldsJson) {
      try {
        fields = defaultSignatureFields(JSON.parse(dto.fieldsJson) as Partial<EmailSignatureFieldValues>);
      } catch {
        // ignore invalid json
      }
    }
    return {
      id: String(dto.id),
      name: dto.name,
      layoutId: (dto.layoutId as EmailSignatureLayoutId) || 'classic',
      html: dto.html,
      fields: this.withAuthPhoto(fields),
      customHtml: !!dto.customHtml,
      updatedAt: dto.updatedAt ? Date.parse(dto.updatedAt) : Date.now()
    };
  }

  private companyDefaults(): Partial<EmailSignatureFieldValues> {
    return {
      companyName: this.companyName() || 'Empresa',
      email: this.companyEmail() || '',
      phone: this.companyPhone() || '',
      logoUrl: this.logoUrl() || '',
      photoUrl: this.authService.getUserPicture() || '',
      introHtml: '',
      bodyHtml: '<p>Escribe aquí el texto de tu firma.</p>',
      signImageUrl: '',
      signImageWidth: SIGN_IMAGE_WIDTH_DEFAULT,
      website: '',
      fullName: this.authService.getUserDisplayName() || '',
      jobTitle: '',
      accentColor: '#0ea5e9'
    };
  }

  /** Siempre fuerza la foto del usuario autenticado y el logo de la empresa (no editables). */
  private withAuthPhoto(fields: EmailSignatureFieldValues | Partial<EmailSignatureFieldValues>): EmailSignatureFieldValues {
    return defaultSignatureFields({
      ...fields,
      photoUrl: this.authService.getUserPicture() || '',
      logoUrl: this.logoUrl() || '',
      bodyHtml: fields.bodyHtml ?? '',
      signImageUrl: fields.signImageUrl ?? '',
      signImageWidth: clampSignImageWidth(fields.signImageWidth)
    });
  }

  newSignature(): void {
    if (!this.enabled()) {
      this.toastr.info('Guarda la empresa para gestionar firmas.');
      return;
    }
    this.editingId.set(null);
    this.selectedLayoutId.set(null);
    this.freeEditUnlocked.set(false);
    this.editorHtmlMode.set(false);
    this.editorHtmlSource = '';
    this.editorStep.set('pick-layout');
    this.signatureForm.reset({ name: '', html: '' });
    this.previewHtml.set('');
    this.fieldsForm.reset(defaultSignatureFields(this.companyDefaults()));
    this.openEditorModal();
  }

  startEdit(item: CompanyEmailSignature): void {
    this.editingId.set(item.id);
    this.selectedLayoutId.set(item.layoutId);
    const isFreeHtml = item.layoutId === 'blank' || item.customHtml;
    this.freeEditUnlocked.set(isFreeHtml);
    this.editorHtmlMode.set(isFreeHtml);
    this.editorHtmlSource = item.html;
    this.editorStep.set('edit');
    this.fieldsForm.reset(this.withAuthPhoto(item.fields), { emitEvent: false });

    if (isFreeHtml) {
      this.signatureForm.reset({ name: item.name, html: item.html });
      this.previewHtml.set(
        item.html.includes('{{')
          ? renderSignatureHandlebars(item.html, buildSignaturePreviewContext(item.fields))
          : item.html
      );
    } else {
      this.signatureForm.reset({ name: item.name, html: '' });
      this.regenerateHtmlFromFields();
    }

    this.openEditorModal();
    if (isFreeHtml) {
      // modo HTML libre: no sincronizar a Quill visual
    } else {
      setTimeout(() => this.syncHtmlToEditor(true), 50);
    }
  }

  private openEditorModal(): void {
    this.showEditor.set(true);
    this.editorShown.set(false);
    setTimeout(() => this.editorShown.set(true), 10);
  }

  closeEditor(): void {
    this.editorShown.set(false);
    setTimeout(() => {
      this.showEditor.set(false);
      this.editingId.set(null);
      this.selectedLayoutId.set(null);
      this.freeEditUnlocked.set(false);
      this.editorHtmlMode.set(false);
      this.quillInstance = null;
    }, 200);
  }

  selectLayout(layoutId: EmailSignatureLayoutId): void {
    this.selectedLayoutId.set(layoutId);
    const preset = this.presets.find((p) => p.id === layoutId);
    const isBlank = !!preset?.blank;
    this.freeEditUnlocked.set(isBlank);

    const fields = this.withAuthPhoto({
      ...this.companyDefaults(),
      fullName:
        this.fieldsForm.get('fullName')?.value
        || this.authService.getUserDisplayName()
        || 'Nombre Apellido',
      jobTitle: this.fieldsForm.get('jobTitle')?.value || 'Cargo'
    });
    this.fieldsForm.reset(fields, { emitEvent: false });

    if (isBlank) {
      this.signatureForm.patchValue({
        name: this.signatureForm.get('name')?.value || 'Firma personalizada'
      });
      this.setHtml('<p><br></p>');
    } else {
      const html = buildSignatureHtml(layoutId, fields, 'preview');
      this.signatureForm.patchValue({
        name: this.signatureForm.get('name')?.value || `Firma ${preset?.label ?? ''}`.trim()
      });
      this.setHtml(html);
    }

    this.editorStep.set('edit');
    setTimeout(() => this.syncHtmlToEditor(), 50);
  }

  backToLayouts(): void {
    this.editorStep.set('pick-layout');
    this.editorHtmlMode.set(false);
    this.freeEditUnlocked.set(false);
  }

  unlockFreeEdit(): void {
    // Abrir directo en modo HTML con el HTML del template intacto.
    // Quill no soporta bien tablas/estilos inline de firmas de correo.
    const html = this.signatureForm.get('html')?.value ?? '';
    this.freeEditUnlocked.set(true);
    this.editorHtmlSource = html;
    this.previewHtml.set(html);
    this.editorHtmlMode.set(true);
    this.suppressQuillWriteback = true;
    this.toastr.info('Modo HTML: el formato del template se conserva. El editor visual puede simplificar tablas y estilos.');
  }

  regenerateHtmlFromFields(): void {
    const layoutId = this.selectedLayoutId();
    if (!layoutId || layoutId === 'blank' || this.freeEditUnlocked()) {
      return;
    }
    const fields = this.withAuthPhoto(this.fieldsForm.getRawValue() as EmailSignatureFieldValues);
    this.fieldsForm.patchValue(
      {
        photoUrl: fields.photoUrl,
        logoUrl: fields.logoUrl,
        bodyHtml: fields.bodyHtml,
        signImageUrl: fields.signImageUrl,
        signImageWidth: fields.signImageWidth
      },
      { emitEvent: false }
    );
    const html = buildSignatureHtml(layoutId, fields, 'preview');
    this.setHtml(html);
  }

  onSignImageFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }

    const companyId = this.companyId();
    if (!companyId) {
      this.toastr.info('Guarda la empresa para subir imágenes de firma.');
      return;
    }

    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      this.toastr.error('Solo se permiten imágenes JPG, PNG o WEBP');
      return;
    }

    const MAX_SIZE_MB = 5;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      this.toastr.error(`La imagen no puede superar ${MAX_SIZE_MB} MB`);
      return;
    }

    this.uploadingSignImage.set(true);
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64Data = result.includes(',') ? result.split(',')[1] : result;
      this.companyService
        .uploadSignImage(companyId, {
          imageBase64: base64Data,
          contentType: file.type,
          name: file.name
        })
        .subscribe({
          next: (res) => {
            this.uploadingSignImage.set(false);
            const url = res.body?.url;
            if (!res.ok || !url) {
              this.toastr.error(res.message || 'No se pudo subir la imagen');
              return;
            }
            this.fieldsForm.patchValue({ signImageUrl: url });
            this.regenerateHtmlFromFields();
            this.toastr.success('Imagen subida');
          },
          error: (err) => {
            this.uploadingSignImage.set(false);
            this.toastr.error(err?.error?.message || 'No se pudo subir la imagen');
          }
        });
    };
    reader.onerror = () => {
      this.uploadingSignImage.set(false);
      this.toastr.error('No se pudo leer el archivo');
    };
    reader.readAsDataURL(file);
  }

  removeSignImage(): void {
    this.fieldsForm.patchValue({ signImageUrl: '' });
    this.regenerateHtmlFromFields();
  }

  onSignImageWidthChange(): void {
    const width = clampSignImageWidth(this.fieldsForm.get('signImageWidth')?.value);
    this.fieldsForm.patchValue({ signImageWidth: width }, { emitEvent: false });
    this.regenerateHtmlFromFields();
  }

  private setHtml(html: string, syncForm = true): void {
    const normalized = rewriteSignatureIconUrls(html ?? '');
    if (syncForm) {
      this.signatureForm.patchValue({ html: normalized }, { emitEvent: false });
    }
    this.previewHtml.set(normalized);
  }

  onEditorInit(event: { editor?: QuillEditorInstance }): void {
    this.quillInstance = event.editor ?? null;
    if (this.editorHtmlMode()) {
      return;
    }
    this.syncHtmlToEditor(true);
  }

  private syncHtmlToEditor(silentWriteback = true): void {
    const html = this.signatureForm.get('html')?.value ?? '';
    if (!this.quillInstance) {
      return;
    }
    if (silentWriteback) {
      this.suppressQuillWriteback = true;
    }
    const delta = this.quillInstance.clipboard.convert({ html: html || '<p><br></p>' });
    this.quillInstance.setContents(delta, 'silent');
    if (silentWriteback) {
      setTimeout(() => {
        this.suppressQuillWriteback = false;
      }, 0);
    }
  }

  onEditorContentChange(): void {
    if (!this.quillInstance || this.editorHtmlMode() || this.suppressQuillWriteback) {
      return;
    }
    this.setHtml(this.quillInstance.root.innerHTML);
  }

  toggleEditorHtmlMode(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.editorHtmlMode()) {
      // Salir de HTML → intentar reflejar en Quill, pero el formulario conserva el HTML del textarea.
      this.applyEditorHtmlSource();
      this.editorHtmlMode.set(false);
      return;
    }

    // Entrar a HTML → usar siempre el HTML canónico del formulario, no el DOM de Quill.
    this.syncEditorHtmlSource();
    this.editorHtmlMode.set(true);
  }

  syncEditorHtmlSource(): void {
    this.editorHtmlSource = this.signatureForm.get('html')?.value ?? '';
    this.previewHtml.set(this.editorHtmlSource);
  }

  applyEditorHtmlSource(): void {
    const html = this.editorHtmlSource;
    this.setHtml(html);
    this.syncHtmlToEditor(true);
  }

  onEditorHtmlSourceChange(): void {
    this.setHtml(this.editorHtmlSource);
  }

  listPreviewHtml(item: CompanyEmailSignature): SafeHtml {
    const source = rewriteSignatureIconUrls(item.html);
    const rendered = source.includes('{{')
      ? renderSignatureHandlebars(source, buildSignaturePreviewContext(item.fields))
      : source;
    return this.sanitizer.bypassSecurityTrustHtml(rendered || '');
  }

  layoutLabel(layoutId: EmailSignatureLayoutId): string {
    return this.presets.find((p) => p.id === layoutId)?.label ?? layoutId;
  }

  saveSignature(): void {
    if (this.editorHtmlMode()) {
      this.setHtml(this.editorHtmlSource);
    } else if (this.quillInstance && !this.suppressQuillWriteback) {
      const formHtml = this.signatureForm.get('html')?.value ?? '';
      if (!formHtml.includes('<table') && !formHtml.includes('style=') && !formHtml.includes('{{')) {
        this.setHtml(this.quillInstance.root.innerHTML);
      }
    }

    this.signatureForm.markAllAsTouched();
    const layoutId = this.selectedLayoutId() ?? 'blank';
    const isFreeHtml = this.freeEditUnlocked() || layoutId === 'blank';
    const fields = this.withAuthPhoto(this.fieldsForm.getRawValue() as EmailSignatureFieldValues);

    let html: string;
    if (isFreeHtml) {
      html = rewriteSignatureIconUrls((this.signatureForm.get('html')?.value as string) ?? '');
    } else {
      html = buildSignatureHtml(layoutId, fields, 'handlebars');
    }

    if (!html.trim() && layoutId !== 'blank') {
      this.toastr.warning('Completa el contenido de la firma.');
      return;
    }
    if (!(this.signatureForm.get('name')?.value as string)?.trim()) {
      this.signatureForm.get('name')?.markAsTouched();
      this.toastr.warning('Completa el nombre y el contenido de la firma.');
      return;
    }
    if (isFreeHtml && !html.trim()) {
      this.toastr.warning('Completa el nombre y el contenido de la firma.');
      return;
    }

    const request = {
      name: (this.signatureForm.get('name')?.value as string).trim(),
      layoutId,
      html,
      fieldsJson: JSON.stringify(fields),
      customHtml: isFreeHtml
    };

    const editingId = this.editingId();
    const numericId = editingId && /^\d+$/.test(editingId) ? Number(editingId) : null;
    this.saving.set(true);

    const req$ = numericId
      ? this.emailSignatureService.update(numericId, request)
      : this.emailSignatureService.create(request);

    req$.subscribe({
      next: (res) => {
        this.saving.set(false);
        if (!res.ok || !res.body) {
          this.toastr.error(apiFailureMessage(res, 'No se pudo guardar la firma'));
          return;
        }
        const saved = this.fromDto(res.body);
        this.signatures.update((list) => {
          const idx = list.findIndex((s) => s.id === saved.id);
          if (idx >= 0) {
            const next = [...list];
            next[idx] = saved;
            return next;
          }
          return [saved, ...list];
        });
        this.toastr.success(numericId ? 'Firma actualizada' : 'Firma creada');
        this.closeEditor();
      },
      error: (err) => {
        this.saving.set(false);
        this.toastr.error(httpErrorApiMessage(err, 'No se pudo guardar la firma'));
      }
    });
  }

  openDelete(item: CompanyEmailSignature): void {
    this.deleteItem.set(item);
    this.deleteConfirmOpen.set(true);
    this.deleteConfirmShown.set(false);
    setTimeout(() => this.deleteConfirmShown.set(true), 10);
  }

  closeDelete(): void {
    this.deleteConfirmShown.set(false);
    setTimeout(() => {
      this.deleteConfirmOpen.set(false);
      this.deleteItem.set(null);
    }, 200);
  }

  confirmDelete(): void {
    const item = this.deleteItem();
    if (!item) {
      return;
    }
    if (!/^\d+$/.test(item.id)) {
      this.signatures.update((list) => list.filter((s) => s.id !== item.id));
      this.toastr.success('Firma eliminada');
      this.closeDelete();
      return;
    }
    this.emailSignatureService.delete(Number(item.id)).subscribe({
      next: (res) => {
        if (!res.ok) {
          this.toastr.error(apiFailureMessage(res, 'No se pudo eliminar la firma'));
          return;
        }
        this.signatures.update((list) => list.filter((s) => s.id !== item.id));
        this.toastr.success('Firma eliminada');
        this.closeDelete();
      },
      error: (err) => {
        this.toastr.error(httpErrorApiMessage(err, 'No se pudo eliminar la firma'));
      }
    });
  }

  copyHtml(item: CompanyEmailSignature): void {
    void navigator.clipboard.writeText(item.html).then(
      () => this.toastr.success('Plantilla Handlebars copiada al portapapeles'),
      () => this.toastr.error('No se pudo copiar la plantilla')
    );
  }
}
